import React, { useMemo, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import {
  Flex,
  FlexItem,
  Select,
  RadioButtonsGroup,
  Grid,
  GridCell,
  GRID_VARIANT_SMALL,
} from 'mailup-web-components'
import { filterFormImmerAtom } from '../state/state'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type IconProp } from '@fortawesome/fontawesome-svg-core'
import { faTrashCan } from 'mailup-fontawesome-kit/classic/regular'
import { type ConditionProps } from '../types'
import { getDefaultSpecByConditionType } from '../utils/conditionUtils'
import { ActionType } from '../../../types/FilterCondition'
import { logAppError } from '../../../helpers/logger'
import ConditionFactory from './ConditionFactory'
import {
  actionTypeOptions,
  conditionTypeGroups,
  conditionTypeOptions,
} from '../utils/conditionOptions'
import { type UiConditionType } from '../filterConditionTypes'

const Condition: FC<ConditionProps> = ({ blockIndex, condIndex }) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const condType = condition.ConditionType

  const actionTypeItems = useMemo(() => actionTypeOptions(intl), [intl])
  const options = useMemo(() => conditionTypeOptions(intl), [intl])
  const groups = useMemo(() => conditionTypeGroups(intl), [intl])

  const removeCondition = (): void => {
    setFilter((draft) => {
      const conditions = draft.Blocks[blockIndex].Conditions
      if (condIndex === 0 && conditions.length > 1) {
        conditions[1].JoinType = ActionType.Nil
      }
      conditions.splice(condIndex, 1)
    })
  }
  const changeActionType = (value: ActionType): void => {
    setFilter((draft) => {
      draft.Blocks[blockIndex].Conditions[condIndex].JoinType = value
    })
  }

  const handleCondType = (value: UiConditionType): void => {
    const defaultSpec = getDefaultSpecByConditionType(value)
    if (defaultSpec === undefined) {
      logAppError({
        Component: 'CombinedFilterFormPage',
        Method: 'setSpecBasedOnCondType',
        appError: new Error(`ConditionType ${value} not recognised`),
      })
    } else {
      setFilter((draft) => {
        const cond = draft.Blocks?.[blockIndex]?.Conditions?.[condIndex]
        if (cond != null) {
          // always true
          cond.ConditionType = value
          cond.Spec = defaultSpec
        }
      })
    }
  }

  const isNotTheOnlyConditionInBlock = (): boolean => {
    const block = filter.Blocks[blockIndex]
    return block.Conditions.length > 1
  }

  console.log("###condType", condType)

  return (
    <>
      {condIndex !== 0 && (
        <Flex flexDirection='row' margin='0px 0px 16px'>
          <RadioButtonsGroup
            items={actionTypeItems}
            value={condition.JoinType}
            onChange={changeActionType}
            name={`actionTypes_block-${blockIndex}_cond-${condIndex}`}
          />
        </Flex>
      )}
      <Flex flexDirection='row' gap='24px' flexGrow={1} margin='0px 0px 16px'>
        <FlexItem flexGrow={1}>
          <Grid variant={GRID_VARIANT_SMALL} cols={12}>
            <GridCell span={3}>
              <Select
                placeholder={intl.formatMessage({
                  id: 'Load.Filters.SelectCondition',
                })}
                onChange={handleCondType}
                options={options}
                optionGroups={groups}
                value={condType}
              />
            </GridCell>
            {condType !== undefined && (
              <ConditionFactory
                conditionType={condType}
                blockIndex={blockIndex}
                condIndex={condIndex}
              />
            )}
          </Grid>
        </FlexItem>
        <FlexItem flexGrow={0} margin='8px 0px'>
          {isNotTheOnlyConditionInBlock() && (
            <FontAwesomeIcon
              icon={faTrashCan as IconProp}
              onClick={removeCondition}
              cursor='pointer'
              data-mu-test='condition-remove'
            />
          )}
        </FlexItem>
      </Flex>
    </>
  )
}

export default Condition
